import dayjs from 'dayjs';

import {
  DependantDetailsMapper,
  DependentDetails,
  IViewFactFindPersonalDetailsMapper,
  IViewFactFindPersonalDetailsResponse,
  PersonalDetailsClient,
} from '@zeta-types';
import { getYesNoObject } from '../common/commons';
import { defaultValues } from './form-helpers';

const getDate = (date: dayjs.ConfigType) => (date ? dayjs(date).toDate() : null);

const mapPersonalDetails = (details: PersonalDetailsClient, sameAsClient = false) => {
  const taxResidency = details.taxDetails?.map((tax) => ({
    id: tax.id || '',
    country: tax.country || '',
    tin: tax.tin || '',
    residencyType: tax.taxResidencyType || '',
    isPrimary: tax.isPrimary !== null ? getYesNoObject(tax.isPrimary ? 'yes' : 'no') : null,
    show: true,
  }));

  const addresses = details.addresses?.map((address, index) => ({
    id: address.id || '',
    addressType: address.addressType || '',
    addressLine1: address.addressLine1 || '',
    addressLine2: address.addressLine2 || '',
    city: address.city || '',
    country: address.country || '',
    postcode: address.postcode || '',
    sameAsClient: index === 0 ? sameAsClient : false,
    show: true,
  }));

  let employmentDetails: any = [];

  employmentDetails = details.employmentDetails?.map((employmentDetail) => ({
    id: employmentDetail.id || '',
    employmentStatus: employmentDetail.employmentStatus || '',
    employmentType: employmentDetail.employmentType || '',
    jobTitle: employmentDetail.jobTitle || '',
    employerName: employmentDetail.employerName || '',
    jobDescription: employmentDetail.jobDescription || '',
    employmentStartDate: getDate(employmentDetail.startDate) || '',
    employmentEndDate: getDate(employmentDetail?.endDate) || '',
    department: employmentDetail.department || '',
    workLocation: employmentDetail.workLocation || '',
    show: true,
  }));

  if (details.employmentDetails?.length === 0) {
    employmentDetails.push({
      id: '',
      jobTitle: '',
      employerName: '',
      employmentStatus: '',
      employmentType: '',
      jobDescription: '',
      employmentStartDate: '',
      employmentEndDate: '',
      department: '',
      workLocation: '',
      show: true,
    });
  }

  return {
    clientID: details.id || '',
    id: details.personalPiiDetails?.id || '',
    title: details.personalPiiDetails?.title || '',
    firstname: details.personalPiiDetails?.firstName?.trim() || '',
    middlename: details.personalPiiDetails?.middleName?.trim() || '',
    lastname: details.personalPiiDetails?.lastName?.trim() || '',
    dob: getDate(details.personalPiiDetails?.dateOfBirth) || null,
    age: dayjs().diff(dayjs(details.personalPiiDetails?.dateOfBirth), 'year') || '',
    email: details.personalPiiDetails?.email || '',
    gender: details?.personalPiiDetails?.gender || '',
    nationality: Array.isArray(details.personalPiiDetails?.nationality)
      ? details.personalPiiDetails?.nationality?.join?.(', ')
      : details.personalPiiDetails?.nationality,
    isUSCitizen:
      details.personalPiiDetails?.isUsCitizen !== null && details.personalPiiDetails?.isUsCitizen !== undefined
        ? getYesNoObject(details.personalPiiDetails?.isUsCitizen ? 'yes' : 'no')
        : undefined,
    citizenship: details.personalPiiDetails?.citizenship || '',
    countryOfBirth: details?.personalPiiDetails?.countryOfBirth || '',
    placeOfBirth: details.personalPiiDetails?.placeOfBirth || '',
    niNumber: details.personalPiiDetails?.niNumber || details.personalPiiDetails?.nino || '',
    marital: details.personalPiiDetails?.martialStatus || details.personalPiiDetails?.marital || '',
    phone: details.personalPiiDetails?.telephoneNumber || '',
    mobile: details.personalPiiDetails?.mobileNumber || '',
    dateOfDeath: getDate(details.personalPiiDetails?.dateOfDeath) || null,
    taxResidency,
    addresses,
    employmentDetails,
  };
};

const mapDependentValue = (details: DependentDetails): DependantDetailsMapper => {
  const piidata = details.personalPiiDetails || details;
  return {
    id: details.personalPiiDetails?.id || details.id || '',
    fullName:
      `${piidata?.firstName ? piidata?.firstName : ''}${piidata?.middleName ? ` ${piidata?.middleName}` : ''}${piidata?.lastName ? ` ${piidata?.lastName}` : ''}`.trim(),
    age: dayjs().diff(dayjs(piidata?.dateOfBirth), 'year').toString() || '',
    gender: piidata?.gender || '',
    startDate: getDate(details?.startDate) || null,
    endDate: getDate(details?.endDate) || null,
    cost: details.costOfSchoolAmount?.toString() || '',
    dateOfBirth: getDate(piidata?.dateOfBirth) || null,
    schoolName: details.schoolName || '',
    show: true,
  };
};

export const setFormData = (data: IViewFactFindPersonalDetailsResponse, sameAsClient?: boolean): IViewFactFindPersonalDetailsMapper => {
  const clientData: PersonalDetailsClient = data?.clientDetails ? mapPersonalDetails(data.clientDetails) : defaultValues;
  const partnerData: PersonalDetailsClient = data?.partnerDetails ? mapPersonalDetails(data.partnerDetails, sameAsClient) : defaultValues;
  const dependentData: DependantDetailsMapper[] = data?.dependentDetails?.map((dependent: DependentDetails) => mapDependentValue(dependent));

  return {
    client: clientData,
    partner: partnerData,
    dependents: dependentData,
  };
};
